/* Danger zone — the operations that cannot be taken back, and the counts that
 * say what they would take with them.
 *
 * The diagnostics are on this page rather than on Overview on purpose. Every
 * action below is measured in rows, and a purge offered without the number
 * beside it is a button somebody presses to find out what it does.
 *
 * Order is deliberate: reversible things first, then the ones that destroy data
 * for one day, then a range, then the whole salon. Nothing here is a link to
 * somewhere else — a destructive action reached by a link is a destructive
 * action reached by accident.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { ConfirmButton } from '../components/confirm-button.js';

// The tables a salon's data actually lives in, in the order they are worth
// reading. `admin_audit` is deliberately absent: it survives the tenant on
// purpose (its foreign key is `on delete set null`) and is not this salon's
// data — it is ours, about them.
var TABLES = [
  { name: 'tt_turns', label: 'Turns' },
  { name: 'tt_days', label: 'Days opened' },
  { name: 'tt_day_staff', label: 'Day rosters' },
  { name: 'tt_staff', label: 'Staff' },
  { name: 'tt_services', label: 'Services' },
  { name: 'tt_settings', label: 'Settings rows' },
  { name: 'tt_service_map', label: 'Service mappings' },
  { name: 'tt_option_rules', label: 'Option rules' },
  { name: 'tt_appt_requests', label: 'Marked requests' },
  { name: 'tt_missed_walkins', label: 'Missed walk-ins' },
  { name: 'appointments', label: 'Appointments (mirror)' },
  { name: 'appointment_services', label: 'Booked services (mirror)' },
  { name: 'clients', label: 'Clients (mirror)' },
  { name: 'sales', label: 'Sales (mirror)' },
  { name: 'tt_channel_messages', label: 'Channel messages' },
  { name: 'tt_channel_reservations', label: 'Channel reservations' }
];

var Danger = {
  name: 'AdminDanger',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var counts = Vue.ref({});
    var counting = Vue.ref(false);

    var reason = Vue.ref('');
    var resetDate = Vue.ref(store.today());
    var purgeFrom = Vue.ref('');
    var purgeTo = Vue.ref('');
    var confirmName = Vue.ref('');

    /* Counted with `head: true`, so PostgREST returns the number in the
     * Content-Range header and no rows at all. Sixteen HEAD requests is a lot of
     * round trips for one panel, which is why this does not run on mount — it
     * runs when somebody asks, on a page nobody opens by accident. */
    async function countAll() {
      var id = store.selectedId.value;
      if (!id) return;
      counting.value = true;
      var out = {};
      for (var i = 0; i < TABLES.length; i++) {
        var t = TABLES[i];
        var res = await api.sb.from(t.name)
          .select('tenant_id', { count: 'exact', head: true }).eq('tenant_id', id);
        out[t.name] = res.error ? null : res.count;
      }
      counts.value = out;
      counting.value = false;
    }

    Vue.watch(store.selectedId, function () { counts.value = {}; });

    async function suspend() {
      await store.setSuspended(true, reason.value.trim() || null);
      reason.value = '';
      store.loadTenants();
    }

    async function resume() {
      await store.setSuspended(false);
      store.loadTenants();
    }

    async function resetDay() {
      var id = store.selectedId.value;
      var n = await api.rpc('admin_reset_day', { p_tenant: id, p_date: resetDate.value });
      if (n == null) return;
      api.toast('Deleted ' + n + ' turn' + (n === 1 ? '' : 's') + ' and reset that day’s counters.');
      store.loadTenants();
      countAll();
    }

    async function purge() {
      var id = store.selectedId.value;
      if (!purgeFrom.value || !purgeTo.value) {
        api.toast('A purge needs both dates.', 'bad');
        return;
      }
      var n = await api.rpc('admin_purge_turns', {
        p_tenant: id, p_from: purgeFrom.value, p_to: purgeTo.value
      });
      if (n == null) return;
      api.toast('Deleted ' + n + ' turn' + (n === 1 ? '' : 's') + '.');
      store.loadTenants();
      countAll();
    }

    // Every settings row at once. The salon is left exactly as a fresh install
    // behaves — not with defaults written out, but with nothing to read, which
    // is what the console's hydrate treats as the default.
    async function resetSettings() {
      var id = store.selectedId.value;
      var res = await api.deleteRows('tt_settings', id, {});
      if (!res) return;
      store.data.settings = [];
      api.log(id, 'settings.reset', {});
      api.toast('Every setting cleared. The board is on shipped defaults from now.');
      store.loadTenants();
    }

    async function destroy() {
      var t = store.selected.value;
      var res = await api.call('tenants.delete', {
        tenantId: store.selectedId.value,
        confirmName: confirmName.value.trim()
      });
      if (!res) return;
      api.toast('Deleted ' + (t ? t.name : 'the tenant') + ' and everything belonging to it.');
      await store.loadTenants();
      nav.goList();
    }

    var nameMatches = Vue.computed(function () {
      var t = store.selected.value;
      return !!t && confirmName.value.trim() === t.name;
    });

    return {
      t: store.selected,
      tables: TABLES,
      counts: counts,
      counting: counting,
      countAll: countAll,
      reason: reason,
      suspend: suspend,
      resume: resume,
      resetDate: resetDate,
      resetDay: resetDay,
      purgeFrom: purgeFrom,
      purgeTo: purgeTo,
      purge: purge,
      resetSettings: resetSettings,
      confirmName: confirmName,
      nameMatches: nameMatches,
      destroy: destroy,
      stamp: store.stamp
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Danger zone</h2>',
    '    <p class="page__lead">Everything on this page changes or destroys a working',
    '      salon&rsquo;s data. Nothing here is undone by pressing it again.</p>',
    '  </div>',
    '',
    '  <div class="card" :class="t && t.suspended_at ? \'card--warn\' : \'\'">',
    '    <div class="card__head">',
    '      <h3>Suspension</h3>',
    '      <span class="tag" :class="t && t.suspended_at ? \'tag--bad\' : \'tag--set\'">',
    '        {{ t && t.suspended_at ? "suspended" : "live" }}</span>',
    '    </div>',
    '    <p class="hint">A suspended salon reads nothing and writes nothing: the tenant lookup',
    '      every policy runs returns null for its accounts, so the board falls back to its',
    '      local cache and its write queue fills up &mdash; exactly as if the internet were',
    '      down, which is the failure it is already built to survive.</p>',
    '    <p class="hint">Nothing is deleted, and lifting it puts everything back within a second',
    '      or two. It is the only reversible thing on this page.</p>',
    '    <template v-if="t && !t.suspended_at">',
    '      <div class="card__row">',
    '        <input v-model="reason" class="input" type="text" maxlength="120"',
    '          placeholder="Why (recorded in the log)" />',
    '        <confirm-button label="Suspend this salon"',
    '          confirm="Suspend — their board stops working"',
    '          @confirmed="suspend" />',
    '      </div>',
    '    </template>',
    '    <template v-else-if="t">',
    '      <p class="warn-line">Suspended {{ stamp(t.suspended_at) }}.</p>',
    '      <button type="button" class="btn btn--primary" @click="resume">Lift the suspension</button>',
    '    </template>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>What is in here</h3>',
    '      <button type="button" class="btn btn--ghost btn--sm" :disabled="counting"',
    '        @click="countAll">{{ counting ? "Counting…" : "Count rows" }}</button>',
    '    </div>',
    '    <p class="hint">Sixteen counts, one request each, so this runs when asked rather than',
    '      on load. Every action below is measured in these.</p>',
    '    <dl class="facts facts--dense">',
    '      <div v-for="tb in tables" :key="tb.name">',
    '        <dt>{{ tb.label }}</dt>',
    '        <dd class="mono">{{ counts[tb.name] == null ? "—" : counts[tb.name] }}</dd>',
    '      </div>',
    '    </dl>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <h3>Reset a day</h3>',
    '    <p class="hint">Deletes every turn on that business date and puts the missed-turn',
    '      counters back to zero. Who is marked in and any skips are left alone &mdash; that is',
    '      a fact about the floor, not about the rotation.</p>',
    '    <div class="card__row">',
    '      <label class="field">',
    '        <span class="field__label">Business date</span>',
    '        <input v-model="resetDate" class="input" type="date" />',
    '      </label>',
    '      <confirm-button label="Reset that day" confirm="Delete that day’s turns"',
    '        @confirmed="resetDay" />',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <h3>Purge a range of history</h3>',
    '    <p class="hint">Deletes turns between two dates and leaves the counters alone &mdash;',
    '      past days are history and their counters are part of what History reports. This is',
    '      for clearing a salon&rsquo;s trial data before they go live.</p>',
    '    <div class="card__row">',
    '      <label class="field">',
    '        <span class="field__label">From</span>',
    '        <input v-model="purgeFrom" class="input" type="date" />',
    '      </label>',
    '      <label class="field">',
    '        <span class="field__label">To</span>',
    '        <input v-model="purgeTo" class="input" type="date" />',
    '      </label>',
    '      <confirm-button label="Purge that range" confirm="Delete every turn in it"',
    '        :disabled="!purgeFrom || !purgeTo" @confirmed="purge" />',
    '    </div>',
    '    <p class="hint">Both dates are required &mdash; the function refuses an open-ended',
    '      range, because one typo in an open range is a salon&rsquo;s entire history.</p>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <h3>Reset every setting</h3>',
    '    <p class="hint">Deletes all of <code>tt_settings</code> for this salon: branding,',
    '      colours, business hours, board options, the plugin configuration. The board carries',
    '      on with the values it ships with. Staff, services and history are untouched.</p>',
    '    <confirm-button label="Clear every setting"',
    '      confirm="Delete every settings row" @confirmed="resetSettings" />',
    '  </div>',
    '',
    '  <div class="card card--danger">',
    '    <h3>Delete this tenant</h3>',
    '    <p class="hint">Cascades through eleven tables: every turn, the whole mirrored book,',
    '      the channel&rsquo;s messages and reservations, the memberships and the ingest keys.',
    '      There is no snapshot and no undo. The accounts themselves survive &mdash; they are',
    '      auth users and simply belong to no salon afterwards.</p>',
    '    <p class="hint">Type the tenant&rsquo;s name to arm it. The server checks it too: a',
    '      confirmation that only lives in a page is one a mistyped request skips.</p>',
    '    <div class="card__row">',
    '      <input v-model="confirmName" class="input" type="text" spellcheck="false"',
    '        :placeholder="t ? t.name : \'\'" />',
    '      <confirm-button label="Delete this tenant" :disabled="!nameMatches"',
    '        confirm="Delete everything, permanently" @confirmed="destroy" />',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'danger',
  name: 'Danger zone',
  group: 'Salon',
  danger: true,
  page: Danger
});

export { Danger };
